import React, { useEffect, useState } from 'react';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot, doc, deleteDoc, updateDoc, arrayRemove, arrayUnion, orderBy } from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import { toast } from 'react-toastify';
import MeetupCard from './MeetupCard';
import ClubCard from './ClubCard';
import ClubForm from './ClubForm';
import { calculateDistance } from '../utils/geo';

const EmptyState = ({ message, action, showLoginPrompt = true, onLoginPromptClick }) => (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6 bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="flex items-center justify-center h-20 w-20 rounded-full bg-indigo-50 mb-5 shadow-inner">
            <span className="text-5xl drop-shadow-sm">♟️</span>
        </div>
        <h3 className="text-lg font-bold text-gray-800 mb-2">It's quiet here...</h3>
        <p className="text-sm text-gray-500 max-w-sm leading-relaxed mb-6">
            {message} 
            {showLoginPrompt && (
                <button 
                    onClick={onLoginPromptClick} 
                    className="block w-full mt-2 font-medium text-indigo-500 hover:text-indigo-700 hover:underline transition-colors"
                >
                    Please log in to create or join.
                </button>
            )}
        </p>
        {action}
    </div>
);

const SessionList = ({ onViewOnMap, onOpenChat, onTriggerLogin, onOpenCalendar, onEditSession, userLocation, searchRadius }) => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('available');
    const [availableSessions, setAvailableSessions] = useState([]);
    const [scheduledSessions, setScheduledSessions] = useState([]);
    const [hostedSessions, setHostedSessions] = useState([]);
    const [joinedSessions, setJoinedSessions] = useState([]);
    const [clubs, setClubs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showClubForm, setShowClubForm] = useState(false);

    useEffect(() => {
        setIsLoading(true);
        const sessionsRef = collection(db, 'sessions');
        const q = query(sessionsRef, orderBy('createdAt', 'desc'));

        const unsubSessions = onSnapshot(q, (snapshot) => {
            const allSessions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Filter and Sort Sessions
            const sortSessions = (list) => {
                return [...list].sort((a, b) => {
                    const dateA = a.createdAt?.toDate() || new Date(0);
                    const dateB = b.createdAt?.toDate() || new Date(0);
                    return dateB - dateA; // Newest first
                });
            };

            const hosted = [];
            const joined = [];
            const available = [];
            const scheduled = [];
            const now = new Date();

            allSessions.forEach(session => {
                let isActive = true;
                if (session.type === 'now') {
                    if (session.availableUntil) {
                        const until = session.availableUntil.toDate?.() || new Date(session.availableUntil);
                        if (until < now) isActive = false;
                    } else if (session.createdAt) {
                        const created = session.createdAt.toDate?.() || new Date(session.createdAt);
                        const hoursSince = (now - created) / (1000 * 60 * 60);
                        if (hoursSince > 4) isActive = false;
                    }
                } else if (session.type === 'scheduled') {
                    if (session.scheduledStartTime) {
                        const startTime = session.scheduledStartTime.toDate?.() || new Date(session.scheduledStartTime);
                        const endTime = new Date(startTime.getTime() + 3 * 60 * 60 * 1000); // 3 hours duration
                        if (now > endTime) isActive = false;
                    } else if (session.createdAt) {
                        // Fallback for scheduled without start time (rare/buggy)
                        const created = session.createdAt.toDate?.() || new Date(session.createdAt);
                        const hoursSince = (now - created) / (1000 * 60 * 60);
                        if (hoursSince > 4) isActive = false;
                    }
                }

                // Allow expired sessions if they are hosted/joined so users can see history or delete them
                const includeInHistory = session.uid === user?.uid || (session.participants || []).includes(user?.uid);

                if (!isActive && !includeInHistory) return;

                // 1. My Hosted Games
                if (user && session.uid === user.uid) {
                    hosted.push(session);
                }

                // 2. My Joined Games
                if (user && (session.participants || []).includes(user.uid)) {
                    joined.push(session);
                }

                // 3. Public Lists (Available / Scheduled)
                // Filter out games I am hosting/joined? No, showing them is better feedback.
                if (isActive || session.type === 'scheduled') {
                    // Note: scheduled sessions also need to check isActive logic if we want to hide old ones, 
                    // but currently isActive mostly handles 'now' logic. 
                    // Let's rely on type.

                    if (session.type === 'scheduled') {
                        scheduled.push(session);
                    } else if (session.type === 'now' && isActive) {
                        available.push(session);
                    }
                }
            });

            setHostedSessions(sortSessions(hosted));
            setJoinedSessions(sortSessions(joined));
            setAvailableSessions(sortSessions(available));
            setScheduledSessions(sortSessions(scheduled));
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching sessions:", error);
            setIsLoading(false);
            if (error.code === 'permission-denied') {
                toast.error("Please login to see sessions.");
            }
        });

        // Fetch clubs independently
        const clubsRef = collection(db, 'clubs');
        const qClubs = query(clubsRef, orderBy('createdAt', 'desc'));
        const unsubClubs = onSnapshot(qClubs, (snapshot) => {
            const allClubs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Filter clubs: Show only approved, unless I am the owner or I am an admin
            const visibleClubs = allClubs.filter(club => {
                if (club.status === 'approved') return true;
                if (user && (user.uid === club.ownerId || user.isAdmin)) return true;
                return false;
            });

            setClubs(visibleClubs);
        }, (error) => {
            console.error("Error fetching clubs:", error);
        });

        return () => {
            unsubSessions();
            unsubClubs();
        };

    }, [user]);
    const handleActionProtected = (action) => {
        if (!user) {
            onTriggerLogin();
            return;
        }
        action();
    };

    const handleDeleteSession = async (sessionId) => {
        // Only hosts can delete, so user must exist, but good safety
        handleActionProtected(async () => {
            if (window.confirm('Are you sure you want to permanently delete this meetup?')) {
                try {

                    await deleteDoc(doc(db, 'sessions', sessionId));

                    toast.success('Meetup deleted.');
                } catch (error) {
                    console.error("Error deleting meetup: ", error);
                    toast.error('Failed to delete meetup.');
                }
            }
        });
    };

    const handleLeaveSession = async (sessionId) => {
        handleActionProtected(async () => {
            if (window.confirm('Are you sure you want to leave this meetup?')) {
                try {
                    const sessionRef = doc(db, 'sessions', sessionId);
                    await updateDoc(sessionRef, {
                        participants: arrayRemove(user.uid)
                    });
                    toast.success('Left meetup.');
                } catch (error) {
                    console.error("Error leaving meetup: ", error);
                    toast.error('Failed to leave meetup.');
                }
            }
        });
    };

    const handleJoinSession = async (sessionId) => {
        handleActionProtected(async () => {
            try {
                const sessionRef = doc(db, 'sessions', sessionId);
                await updateDoc(sessionRef, {
                    participants: arrayUnion(user.uid)
                });
                toast.success('You joined the game!');
                setActiveTab('joined');
            } catch (error) {
                console.error("Error joining session:", error);
                toast.error("Failed to join session.");
            }
        });
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="space-y-4 animate-pulse">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 mb-3 opacity-60">
                            <div className="flex gap-3 items-start">
                                <div className="w-10 h-10 rounded-full bg-gray-200 shrink-0"></div>
                                <div className="flex-1 space-y-2 py-1">
                                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                                    <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                                </div>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-2">
                                <div className="h-12 bg-gray-50 rounded"></div>
                                <div className="h-12 bg-gray-50 rounded"></div>
                            </div>
                            <div className="mt-4 flex gap-2">
                                <div className="h-8 bg-gray-100 rounded w-full"></div>
                                <div className="h-8 bg-gray-100 rounded w-full"></div>
                                <div className="h-8 bg-gray-100 rounded w-full"></div>
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        if (activeTab === 'clubs') {
            if (showClubForm) {
                return (
                    <ClubForm
                        onCancel={() => setShowClubForm(false)}
                        onSuccess={() => setShowClubForm(false)}
                    />
                );
            }

            const filteredClubs = clubs.filter(club => {
                if (!userLocation || !searchRadius || searchRadius >= 10000) return true;
                let lat, lng;
                if (club.location && typeof club.location === 'string') {
                    [lat, lng] = club.location.split(',').map(Number);
                } else if (club.location?.latitude) {
                    lat = club.location.latitude;
                    lng = club.location.longitude;
                }
                if (!lat || !lng || isNaN(lat)) return true;
                const dist = calculateDistance(userLocation.lat, userLocation.lng, lat, lng);
                return dist <= searchRadius;
            });

            if (filteredClubs.length === 0 && clubs.length === 0) {
                return (
                    <EmptyState
                        onLoginPromptClick={onTriggerLogin}
                        message="No clubs listed yet. Do you run a local club?"
                        action={
                            <button
                                onClick={() => handleActionProtected(() => setShowClubForm(true))}
                                className="mt-2 bg-indigo-600 text-white px-6 py-2.5 rounded-full font-semibold hover:bg-indigo-700 transition-colors shadow-md hover:shadow-lg focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                + Register a Club
                            </button>
                        }
                    />
                );
            }

            if (filteredClubs.length === 0) {
                return (
                    <EmptyState
                        onLoginPromptClick={onTriggerLogin}
                        message={`No clubs found within ${searchRadius} miles.`}
                        action={
                            <button
                                onClick={() => handleActionProtected(() => setShowClubForm(true))}
                                className="mt-2 bg-indigo-600 text-white px-6 py-2.5 rounded-full font-semibold hover:bg-indigo-700 transition-colors shadow-md hover:shadow-lg focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                + Register a Club
                            </button>
                        }
                    />
                );
            }

            return (
                <div className="space-y-3">
                    <button
                        onClick={() => handleActionProtected(() => setShowClubForm(true))}
                        className="w-full py-2 border-2 border-dashed border-indigo-300 text-indigo-600 rounded-lg hover:bg-indigo-50 font-medium mb-2"
                    >
                        + Register a New Club
                    </button>
                    {filteredClubs.map(club => (
                        <ClubCard
                            key={club.id}
                            club={club}
                            onViewOnMap={onViewOnMap}
                            onOpenChat={s => handleActionProtected(() => onOpenChat(s))}
                        />
                    ))}
                </div>
            );
        }

        let sessions = [];
        let emptyMsg = "";

        switch (activeTab) {
            case 'available':
                sessions = availableSessions;
                emptyMsg = "No active games available right now. Be the first to start one!";
                break;
            case 'scheduled':
                sessions = scheduledSessions;
                emptyMsg = "No scheduled games coming up.";
                break;
            case 'hosted':
                sessions = hostedSessions;
                emptyMsg = "You haven't started any games yet.";
                break;
            case 'joined':
                sessions = joinedSessions;
                emptyMsg = "You haven't joined any games yet.";
                break;
            default:
                sessions = [];
        }

        const filteredSessions = sessions.filter(session => {
            if (!userLocation || !searchRadius || searchRadius >= 10000) return true;
            let lat, lng;
            if (session.location && typeof session.location === 'string') {
                [lat, lng] = session.location.split(',').map(Number);
            } else if (session.location?.latitude) {
                lat = session.location.latitude;
                lng = session.location.longitude;
            }
            if (!lat || !lng || isNaN(lat)) return true;
            return calculateDistance(userLocation.lat, userLocation.lng, lat, lng) <= searchRadius;
        });

        if (filteredSessions.length === 0) {
            const finalEmptyMsg = sessions.length > 0 
                ? `No games found within ${searchRadius} miles. Try zooming out!` 
                : emptyMsg;
            return <EmptyState onLoginPromptClick={onTriggerLogin} message={finalEmptyMsg} showLoginPrompt={!user} />;
        }

        return filteredSessions.map(session => (
            <MeetupCard
                key={session.id}
                session={session}
                onViewOnMap={onViewOnMap}
                onOpenChat={(s) => handleActionProtected(() => onOpenChat(s))}
                onDelete={handleDeleteSession}
                onLeave={handleLeaveSession}
                onJoin={handleJoinSession}
            />
        ));
    };

    return (
        <div id="session-list" className="bg-white rounded-xl shadow-lg overflow-hidden flex flex-col h-[400px] lg:h-[600px]">
            <div className="p-4 border-b bg-white z-10">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">Find Games</h2>
                    <button
                        onClick={onOpenCalendar}
                        className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2"
                    >
                        📅 <span className="hidden xs:inline">Calendar</span>
                    </button>
                </div>
                <div className="flex bg-gray-100 p-1 rounded-lg overflow-x-auto">
                    {['available', 'scheduled', ...(user ? ['hosted', 'joined'] : []), 'clubs'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => { setActiveTab(tab); setShowClubForm(false); }}
                            className={`flex-1 min-w-[70px] py-1.5 text-xs font-medium rounded-md transition-all capitalize whitespace-nowrap ${activeTab === tab
                                ? 'bg-white text-indigo-600 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            {tab === 'available' ? 'Active' : (tab === 'clubs' ? 'Clubs & Recurring' : tab)}
                            <span className="ml-1 text-[9px] opacity-75">
                                {tab === 'available' && availableSessions.length > 0 && `(${availableSessions.length})`}
                                {tab === 'scheduled' && scheduledSessions.length > 0 && `(${scheduledSessions.length})`}
                                {tab === 'hosted' && hostedSessions.length > 0 && `(${hostedSessions.length})`}
                                {tab === 'joined' && joinedSessions.length > 0 && `(${joinedSessions.length})`}
                                {tab === 'clubs' && clubs.length > 0 && `(${clubs.length})`}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                {renderContent()}
            </div>
        </div>
    );
}

export default SessionList;
